// What a pinned SDK's React `./app` entry gives the app shell: its companion's pages (the companion
// "full page" routes at /<path>, which the app links to from its own UI) and an overlay the shell mounts on every page
// (the tour host). The shell imports no SDK: the project's web/main.tsx passes the entries of the
// SDKs it pins.
import * as React from 'react';

/** The window events the shell and the SDKs' overlays share (the same names as cortex's). */
export const OPEN_CHAT_EVENT = 'chat:open';
export const START_TUTORIAL_EVENT = 'cortex:start-tutorial';
/**
 * An app's viewer options changed (its declaration's companions or chat kind): the speed dial and the chat
 * over that app read them again, so a save shows at once instead of after a reload.
 */
export const COMPANIONS_CHANGED_EVENT = 'cortex:companions-changed';
export interface CompanionsChangedDetail {
  slug: string;
  app: string;
}
export const companionsChangedEvent = (detail: CompanionsChangedDetail) =>
  new CustomEvent<CompanionsChangedDetail>(COMPANIONS_CHANGED_EVENT, { detail });
/** Calls `run` on each change of this app's viewer options (any app when `app` is null); answers the unsubscribe. */
export function onCompanionsChanged(slug: string, app: string | null, run: () => void): () => void {
  const on = (e: Event) => {
    const d = (e as CustomEvent<Partial<CompanionsChangedDetail>>).detail ?? {};
    if (d.slug === slug && (app === null || d.app === app)) run();
  };
  window.addEventListener(COMPANIONS_CHANGED_EVENT, on);
  return () => window.removeEventListener(COMPANIONS_CHANGED_EVENT, on);
}

/** Calls `run` whenever the viewer options of this app change (any app when `app` is null). */
export function useCompanionsChanged(slug: string, app: string | null, run: () => void) {
  const ref = React.useRef(run);
  ref.current = run;
  React.useEffect(() => onCompanionsChanged(slug, app, () => ref.current()), [slug, app]);
}

export interface CompanionOverlayProps {
  slug: string;
  /** The app open on the page (its key), or null. */
  app: string | null;
}

export interface AppCompanion {
  /** The SDK module (`workflows`): only the companions of the app's pinned SDKs are offered. */
  module: string;
  /** Its pages by path (`workflows`, `models/:modelName/run`): at /<path> on an app's own page. */
  pages?: Record<string, () => Promise<React.ComponentType>>;
  /**
   * Mounted once on every page (the tour host). It draws nothing over the app until the app asks (no launcher, no
   * window: companion UI is off for every app by default; the app draws its own entries).
   */
  Overlay?: React.ComponentType<CompanionOverlayProps>;
}

const PagesCtx = React.createContext<Record<string, () => Promise<React.ComponentType>>>({});

export function CompanionPagesProvider({
  pages,
  children,
}: {
  pages: Record<string, () => Promise<React.ComponentType>>;
  children: React.ReactNode;
}) {
  return <PagesCtx.Provider value={pages}>{children}</PagesCtx.Provider>;
}

/** Every page of the pinned companions, by path. */
export const useCompanionPages = () => React.useContext(PagesCtx);
